/**
 * ============================================================================
 * STRATEGIC RELEVANCE — the scoring model
 * ============================================================================
 *
 * ONE model, derived at run time, so the wizard, the dashboard, the portfolio
 * table and the analysis library can never disagree. Partnership Net 2.0
 * carried three inconsistent implementations of this arithmetic plus a seed
 * whose stored scores did not reconcile with its own stored inputs.
 *
 *   dimension score  = Σ(criterion × weight) / Σ(weight)   over enabled criteria
 *   BaseScore        = Σ(dimension × weight) / Σ(weight)    over enabled dimensions
 *   DiscountFactor   = −risk% + potential% + alignment%     (five levels each)
 *   OpportunityScore = BaseScore × (1 + DiscountFactor)
 *
 * Two decisions worth knowing about:
 *
 *   BaseScore is rounded to one decimal BEFORE the discount factor is applied.
 *   That is what makes the arithmetic printed in the "working" panel reconcile
 *   exactly with the OpportunityScore shown on the card. Rounding afterwards
 *   produces a panel whose numbers do not multiply out.
 *
 *   OpportunityScore is deliberately NOT capped at 100. A partnership with
 *   strong potential and low risk can and should score above the base ceiling;
 *   clamping it would hide the thing the factor exists to express.
 */

import type { SrConfiguration, SrFactorKey, SrMatrixDef, SrPartner } from '@/data/contracts';
import type { StatusKind } from '@/lib/bands';
import { round, safeDiv, sum } from '@/lib/math';

/** Assessor slider values, keyed `"<dimensionId>:<criterionName>"`. */
export type SrScores = Readonly<Record<string, number>>;

/** The three factor inputs an assessor sets directly. */
export interface SrFactorInputs {
  readonly risk: number;
  readonly potential: number;
  /** The assessor's "strategic support" slider feeds the alignment factor. */
  readonly support: number;
}

export interface SrFactorResult {
  readonly level: string;
  readonly pct: number;
  /** Signed fraction, e.g. −0.15 for a 15% malus. */
  readonly signed: number;
  readonly index: number;
}

export interface SrComputation {
  readonly dims: readonly SrConfiguration['dims'][number][];
  readonly dimScores: Readonly<Record<string, number>>;
  readonly base: number;
  readonly df: number;
  readonly opp: number;
  readonly risk: SrFactorResult;
  readonly pot: SrFactorResult;
  readonly al: SrFactorResult;
  /** Sum of enabled dimension weights — the BaseScore denominator. */
  readonly wsum: number;
}

/**
 * Maps a 0–100 assessor score to a factor level index.
 *
 *   81–100 → 0 Very High
 *   61– 80 → 1 High
 *   41– 60 → 2 Medium
 *   21– 40 → 3 Low
 *    0– 20 → 4 Very Low
 */
export function srLevelIndex(value: number): number {
  if (value > 80) return 0;
  if (value > 60) return 1;
  if (value > 40) return 2;
  if (value > 20) return 3;
  return 4;
}

/** Resolves a factor input against the configured levels. */
export function srFactor(
  kind: SrFactorKey,
  value: number,
  config: SrConfiguration,
): SrFactorResult {
  const factor = config.factors[kind];
  const index = srLevelIndex(value);
  const level = factor.levels[index] ?? factor.levels.at(-1);
  if (level === undefined) {
    throw new Error(`Strategic Relevance factor "${kind}" has no levels configured.`);
  }
  return {
    level: level[0],
    pct: level[1],
    signed: (factor.sign * level[1]) / 100,
    index,
  };
}

/** Which stored partner field seeds each dimension when no scores are supplied. */
const DIMENSION_INPUT: Readonly<Record<string, (p: SrPartner) => number>> = {
  brand: (p) => p.brand,
  business: (p) => p.business,
  people: (p) => p.people,
  planet: (p) => p.planet,
  // `alignment` has no stored input: it is scored through the wizard only, and
  // is disabled in the published configuration.
};

/** Weighted mean of the enabled criteria in one dimension. */
export function srDimScore(dimensionId: string, scores: SrScores, config: SrConfiguration): number {
  const criteria = (config.criteria[dimensionId] ?? []).filter((c) => c.enabled);
  const weight = sum(criteria.map((c) => c.weight));
  const weighted = sum(criteria.map((c) => (scores[`${dimensionId}:${c.name}`] ?? 0) * c.weight));
  return safeDiv(weighted, weight);
}

/**
 * Computes every score for one partnership.
 *
 * Pass `scores` when the assessor is working through the wizard. Omit it to
 * score from the partnership's stored dimension inputs, which is what the
 * dashboard, the portfolio and the library do.
 */
export function srCompute(
  partner: SrPartner & Partial<SrFactorInputs>,
  config: SrConfiguration,
  scores?: SrScores,
): SrComputation {
  const dims = config.dims.filter((d) => d.enabled);

  const dimScores: Record<string, number> = {};
  for (const d of dims) {
    dimScores[d.id] = scores
      ? srDimScore(d.id, scores, config)
      : (DIMENSION_INPUT[d.id]?.(partner) ?? 0);
  }

  const wsum = sum(dims.map((d) => d.weight));
  // Rounded here, before the factor — see the header note.
  const base = round(safeDiv(sum(dims.map((d) => (dimScores[d.id] ?? 0) * d.weight)), wsum), 1);

  const risk = srFactor('risk', partner.risk, config);
  const pot = srFactor('potential', partner.potential, config);
  const al = srFactor('alignment', partner.support, config);

  const df = risk.signed + pot.signed + al.signed;

  return {
    dims,
    dimScores,
    base,
    df: round(df, 4),
    opp: round(base * (1 + df), 1),
    risk,
    pot,
    al,
    wsum,
  };
}

/* --------------------------------------------------------------- banding -- */

export function srBandKind(score: number): StatusKind {
  if (score >= 80) return 'good';
  if (score >= 60) return 'info';
  if (score >= 40) return 'warn';
  return 'bad';
}

export function srBandLabel(score: number): string {
  if (score >= 80) return 'High';
  if (score >= 60) return 'Medium';
  if (score >= 40) return 'Low';
  return 'Very low';
}

export function srColor(score: number): string {
  if (score >= 80) return 'var(--st-good-bd)';
  if (score >= 60) return 'var(--pnet-blue)';
  if (score >= 40) return 'var(--st-warn-bd)';
  return 'var(--st-bad-bd)';
}

/** Legend rows for the matrix, so every chart states the same bands. */
export const SR_BAND_LEGEND: readonly (readonly [number, string])[] = [
  [85, 'High ≥ 80'],
  [70, 'Medium 60–79'],
  [50, 'Low 40–59'],
  [20, 'Very low < 40'],
];

/**
 * Which quadrant a partnership falls into.
 *
 * Derived from its position against the configured thresholds — never stored,
 * so moving a threshold in Admin moves every partnership immediately.
 */
export function srQuadrant(brand: number, business: number, matrix: SrMatrixDef): string {
  const highBrand = brand >= matrix.yThr;
  const highBusiness = business >= matrix.xThr;
  const [topLeft = '', topRight = '', bottomLeft = '', bottomRight = ''] = matrix.q;
  if (highBrand && !highBusiness) return topLeft;
  if (highBrand && highBusiness) return topRight;
  if (!highBrand && !highBusiness) return bottomLeft;
  return bottomRight;
}

/** Total of the enabled dimension weights. Must be 100 to publish. */
export const srWeightTotal = (config: SrConfiguration): number =>
  sum(config.dims.filter((d) => d.enabled).map((d) => d.weight));

/** Range the discount factor can span under the current magnitudes. */
export function srFactorRange(config: SrConfiguration): { min: number; max: number } {
  const worstRisk = config.factors.risk.levels[0]?.[1] ?? 0;
  const bestPot = config.factors.potential.levels[0]?.[1] ?? 0;
  const bestAl = config.factors.alignment.levels[0]?.[1] ?? 0;
  return { min: round(-worstRisk / 100, 2), max: round((bestPot + bestAl) / 100, 2) };
}

/** Seeds wizard slider values from a partnership's stored dimension inputs. */
export function srInitialScores(partner: SrPartner, config: SrConfiguration): SrScores {
  const scores: Record<string, number> = {};
  for (const d of config.dims) {
    const seed = DIMENSION_INPUT[d.id]?.(partner) ?? 60;
    for (const c of config.criteria[d.id] ?? []) {
      scores[`${d.id}:${c.name}`] = seed;
    }
  }
  return scores;
}

/**
 * Recommendation for one dimension, from its score alone.
 * Kept here rather than in the view so the dashboard and any export agree.
 */
export function srDimensionRecommendation(score: number): {
  readonly text: string;
  readonly importance: 'critical' | 'serious' | 'warning';
} {
  if (score < 50) {
    return {
      text: 'Rebuild the case for this dimension or descope it from the partnership.',
      importance: 'critical',
    };
  }
  if (score < 70) {
    return {
      text: 'Set a measurable improvement target for the next cycle.',
      importance: 'serious',
    };
  }
  return {
    text: 'Protect the position and evidence it in the next review.',
    importance: 'warning',
  };
}

/** Owner for a dimension's action, used by the next-steps table. */
export function srActionOwner(dimensionId: string): string {
  switch (dimensionId) {
    case 'brand':
      return 'Brand team';
    case 'business':
      return 'Commercial';
    case 'people':
      return 'People';
    default:
      return 'Partnership lead';
  }
}

/** Headline next step, from the OpportunityScore. */
export function srNextStep(opp: number): readonly [string, string] {
  if (opp >= 80) {
    return ['Portfolio experience partner', 'Proceed with full investment and integration.'];
  }
  if (opp >= 60) {
    return [
      'High engagement partner',
      'Develop an enhanced activation plan before the next cycle.',
    ];
  }
  return ['Review or exit', 'Consider renegotiation or portfolio optimisation.'];
}
